import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

/**
 * Reusable enterprise-style 3-dot row overflow menu.
 *
 * Consolidates the legacy "View / Archive / Restore / Open Dashboard /
 * Delete" button cluster into a single kebab. Each `actions` entry is a
 * tiny descriptor — the consumer picks visibility + handler; this
 * component renders icon, label, optional `destructive` styling, and
 * separators automatically.
 *
 *   <RowActionsMenu
 *     testId={`row-${id}-actions`}
 *     actions={[
 *       { id: "view",    label: "View Details", icon: Eye, onClick: openDetail },
 *       { id: "dashboard", label: "Open Client Dashboard", icon: LayoutDashboard,
 *         onClick: openDashboard, visible: row.sent_to_customer },
 *       { id: "archive", label: "Archive", icon: Archive, onClick: archive,
 *         visible: !isArchived },
 *       { id: "restore", label: "Restore", icon: ArchiveRestore, onClick: restore,
 *         visible: isArchived },
 *       { id: "delete",  label: "Delete", icon: Trash2, onClick: del,
 *         destructive: true, visible: isAdmin, separatorBefore: true },
 *     ]}
 *   />
 */
export default function RowActionsMenu({ actions = [], testId, ariaLabel }) {
  const visible = (actions || []).filter((a) => a && a.visible !== false);
  if (visible.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          aria-label={ariaLabel || "Row actions"}
          className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-[#6B7280] hover:text-[#111827] hover:bg-[#F3F4F6] focus:outline-none focus:ring-1 focus:ring-[#025979] transition-colors"
        >
          <MoreVertical className="w-4 h-4" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[200px] rounded-sm py-1"
        data-testid={testId ? `${testId}-content` : undefined}
      >
        {visible.map((a, idx) => {
          const Icon = a.icon;
          const node = (
            <DropdownMenuItem
              key={a.id || idx}
              onSelect={(e) => {
                e.preventDefault();
                a.onClick?.();
              }}
              disabled={!!a.disabled}
              data-testid={a.testId}
              className={`flex items-center gap-2 text-xs cursor-pointer rounded-sm ${
                a.destructive
                  ? "text-[#B91C1C] focus:bg-[#FEF2F2] focus:text-[#991B1B]"
                  : "text-[#111827] focus:bg-[#F3F4F6]"
              }`}
            >
              {Icon ? <Icon className="w-3.5 h-3.5" strokeWidth={2} /> : null}
              <span>{a.label}</span>
            </DropdownMenuItem>
          );
          return a.separatorBefore ? (
            <span key={`sep-${a.id || idx}`}>
              <DropdownMenuSeparator />
              {node}
            </span>
          ) : (
            node
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
